import { Avatar, List, SxProps, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  MajorWaterLevelIcon,
  ModerateWaterLevelIcon,
  MinorWaterLevelIcon,
  ActionWaterLevelIcon,
  NormalWaterLevelIcon,
  OfflineWaterLevelIcon,
  NoneWaterLevelIcon,
  ActionForecastMajorIcon,
} from 'features/mapping/icons';
import ForecastNormalIconSrc from 'assets/water-level/forecast-normal.svg';
import ForecastActionIconSrc from 'assets/water-level/forecast-action.svg';
import ForecastMinorIconSrc from 'assets/water-level/forecast-minor.svg';
import ForecastModerateIconSrc from 'assets/water-level/forecast-moderate.svg';
import ForecastMajorIconSrc from 'assets/water-level/forecast-major.svg';
import { LegendListItem } from './LegendListItem';
import { LegendIcon } from './LegendIcon';
import { LegendSectionTitle } from './LegendSectionTitle';
import { getLegendPointAvatarBackgroundStyles } from './LegendDrawerContent.utils';

type LegendDualAvatarProps = {
  primaryIconSrc: string;
  secondaryIconSrc: string;
  primaryAltText: string;
};

const listItemAvatarSx: SxProps<Theme> = {
  mt: 0.5,
};

const LegendDualAvatar = (props: LegendDualAvatarProps): JSX.Element => {
  const { primaryIconSrc, secondaryIconSrc, primaryAltText } = props;
  return (
    <Avatar
      sx={(theme) => ({
        ...getLegendPointAvatarBackgroundStyles(theme),
        flexDirection: 'column',
        gap: 1,
        height: 72,
        borderRadius: 40,
      })}
    >
      <LegendIcon src={primaryIconSrc} alt={primaryAltText} size={22} />
      <LegendIcon src={secondaryIconSrc} alt="" size={22} />
    </Avatar>
  );
};

export const WaterLevelLegends = (): JSX.Element => {
  const { t } = useTranslation();

  return (
    <>
      <List
        disablePadding
        dense
        subheader={
          <LegendSectionTitle>
            {t('map.legends.waterLevel.primaryTitle')}
          </LegendSectionTitle>
        }
      >
        <LegendListItem
          iconSrc={ActionWaterLevelIcon.data}
          primaryText={t(
            'map.legends.waterLevel.legends.observedFloodStage.title',
          )}
          secondary={t(
            'map.legends.waterLevel.legends.observedFloodStage.subtitle',
          )}
          iconSize={22}
        />
        <LegendListItem
          iconSrc={ActionForecastMajorIcon.data}
          primaryText={t(
            'map.legends.waterLevel.legends.forecastedFloodStage.title',
          )}
          secondary={t(
            'map.legends.waterLevel.legends.forecastedFloodStage.subtitle',
          )}
          iconSize={28}
        />
        <LegendListItem
          iconSrc={NoneWaterLevelIcon.data}
          primaryText={t('map.legends.waterLevel.legends.default.title')}
          secondary={t('map.legends.waterLevel.legends.default.subtitle')}
          iconSize={22}
        />
        <LegendListItem
          iconSrc={OfflineWaterLevelIcon.data}
          primaryText={t('map.legends.waterLevel.legends.offline.title')}
          secondary={t('map.legends.waterLevel.legends.offline.subtitle')}
          iconSize={22}
        />
      </List>

      <List
        disablePadding
        dense
        subheader={
          <LegendSectionTitle>
            {t('map.legends.waterLevel.secondaryTitle')}
          </LegendSectionTitle>
        }
      >
        <LegendListItem
          primaryText={t('map.legends.waterLevel.legends.major.title')}
          secondary={t('map.legends.waterLevel.legends.major.subtitle')}
          AvatarComponent={
            <LegendDualAvatar
              primaryIconSrc={MajorWaterLevelIcon.data}
              secondaryIconSrc={ForecastMajorIconSrc}
              primaryAltText={t('map.legends.waterLevel.legends.major.title')}
            />
          }
          listItemAvatarSxProps={listItemAvatarSx}
        />
        <LegendListItem
          primaryText={t('map.legends.waterLevel.legends.moderate.title')}
          secondary={t('map.legends.waterLevel.legends.moderate.subtitle')}
          AvatarComponent={
            <LegendDualAvatar
              primaryIconSrc={ModerateWaterLevelIcon.data}
              secondaryIconSrc={ForecastModerateIconSrc}
              primaryAltText={t(
                'map.legends.waterLevel.legends.moderate.title',
              )}
            />
          }
          listItemAvatarSxProps={listItemAvatarSx}
        />
        <LegendListItem
          primaryText={t('map.legends.waterLevel.legends.minor.title')}
          secondary={t('map.legends.waterLevel.legends.minor.subtitle')}
          AvatarComponent={
            <LegendDualAvatar
              primaryIconSrc={MinorWaterLevelIcon.data}
              secondaryIconSrc={ForecastMinorIconSrc}
              primaryAltText={t('map.legends.waterLevel.legends.minor.title')}
            />
          }
          listItemAvatarSxProps={listItemAvatarSx}
        />
        <LegendListItem
          primaryText={t('map.legends.waterLevel.legends.action.title')}
          secondary={t('map.legends.waterLevel.legends.action.subtitle')}
          AvatarComponent={
            <LegendDualAvatar
              primaryIconSrc={ActionWaterLevelIcon.data}
              secondaryIconSrc={ForecastActionIconSrc}
              primaryAltText={t('map.legends.waterLevel.legends.action.title')}
            />
          }
          listItemAvatarSxProps={listItemAvatarSx}
        />
        <LegendListItem
          primaryText={t('map.legends.waterLevel.legends.normal.title')}
          secondary={t('map.legends.waterLevel.legends.normal.subtitle')}
          AvatarComponent={
            <LegendDualAvatar
              primaryIconSrc={NormalWaterLevelIcon.data}
              secondaryIconSrc={ForecastNormalIconSrc}
              primaryAltText={t('map.legends.waterLevel.legends.normal.title')}
            />
          }
          listItemAvatarSxProps={listItemAvatarSx}
        />
      </List>
    </>
  );
};
